import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Sparkles,
  Heart,
  Brain,
  Smile,
  Monitor,
  Music,
  Users,
  Film,
  Video,
  Puzzle,
  Dumbbell,
  Palette
} from "lucide-react";

const primaryOutcomes = [
  {
    icon: Sparkles,
    title: "Sensation",
    desc: "Touch, sound, colour and movement on the screen engage the senses in ways traditional activities can't."
  },
  {
    icon: Heart,
    title: "Relaxation",
    desc: "Gentle games and calming visuals help residents unwind, offering a soothing, pressure-free experience."
  },
  {
    icon: Smile,
    title: "Reminiscence",
    desc: "Old music, vintage photos, and familiar themes bring memories back to life and spark conversation."
  }
];

const interactiveTableUses = [
  { icon: Brain, label: "Brain training & memory apps" },
  { icon: Puzzle, label: "Group puzzles & games" },
  { icon: Music, label: "Music & sing-alongs" },
  { icon: Dumbbell, label: "Keep-fit sessions & quizzes" },
  { icon: Film, label: "Films & documentaries" },
  { icon: Video, label: "Video calls with family" },
  { icon: Users, label: "Collaborative multi-user play" },
  { icon: Palette, label: "Creative, cognitive & sensory aids" }
];

export const Activities = () => {
  return (
    <section id="activities" className="py-20 bg-gradient-to-b from-white to-teal-50/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-14">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
            Activities &amp; Well-being
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            A Life Full of Moments
          </h2>
          <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Every day at Barnston Court is shaped by a varied programme of activities, designed and led
            by our dedicated Activities Coordinator. A fresh calendar is created each month to keep life
            engaging, personal, and full of purpose.
          </p>
        </div>

        {/* Activities Team + Sensory Stimulation */}
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-14">
          <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-teal-100/60 hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-teal-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">A Dedicated Activities Team</h3>
              <p className="text-gray-700 leading-relaxed">
                Life at Barnston Court is shaped by our dedicated Activities Coordinator, who plans a
                fresh calendar each month tailored to our residents' interests and abilities. From
                gentle exercise and creative sessions to reminiscence, music and outings, every day is
                designed to keep our residents engaged, connected and smiling.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-orange-100/60 hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-orange-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <Heart className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sensory Stimulation for Dementia</h3>
              <p className="text-gray-700 leading-relaxed">
                Many of our residents live with dementia, where daily engagement plays a vital role in
                maintaining cognitive health. Our activities are built around sensory stimulation,
                bringing together touch, taste, hearing, sight and smell to keep minds active and spirits lifted.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Interactive Table Feature */}
        <div className="max-w-6xl mx-auto mb-14">
          <Card className="border-2 border-teal-200 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="bg-white/15 backdrop-blur p-4 rounded-xl">
                  <Monitor className="h-10 w-10" />
                </div>
                <div>
                  <Badge className="bg-white/20 text-white hover:bg-white/30 mb-2">
                    Technology That Connects
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold">
                    The 40-inch Sharp Interactive Table
                  </h3>
                  <p className="text-teal-50 mt-1">Placed at Barnston Court by Cozee Care</p>
                </div>
              </div>
            </div>

            <CardContent className="p-6 md:p-10 bg-white">
              <p className="text-gray-700 leading-relaxed mb-6">
                Cozee Care believes technology has a powerful role in enriching daily life. That's why
                every Cozee Care home is fitted with a 40-inch Sharp Interactive Table, a robust,
                scratch-resistant touchscreen running on Android with up to 10 simultaneous touchpoints.
                That means two residents and a carer can share the experience together, side by side.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Backed by research into dementia and the neuroscience of play, we know that even in
                advanced stages of dementia, residents can still experience three powerful outcomes
                through guided interaction with the table:
              </p>

              {/* 3 Outcomes */}
              <div className="grid sm:grid-cols-3 gap-4 mb-10">
                {primaryOutcomes.map((outcome, index) => {
                  const Icon = outcome.icon;
                  return (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all"
                    >
                      <div className="bg-teal-100 text-teal-700 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h4 className="font-bold text-gray-900 mb-1.5">{outcome.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{outcome.desc}</p>
                    </div>
                  );
                })}
              </div>

              {/* Uses Grid */}
              <h4 className="text-lg font-bold text-gray-900 mb-4">How we use it day-to-day:</h4>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {interactiveTableUses.map((use, index) => {
                  const Icon = use.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors"
                    >
                      <Icon className="h-5 w-5 text-teal-600 flex-shrink-0" />
                      <span className="text-sm text-gray-700 font-medium">
                        {use.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Closing */}
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-gray-700 leading-relaxed italic">
            "At Barnston Court, we believe in enriching the lives of people with dementia so they feel
            happy, valued and fulfilled. Together, our activities calendar, sensory programme and
            interactive technology keep our residents sharp, engaged and smiling, every single day."
          </p>
        </div>
      </div>
    </section>
  );
};
